import { Button, Grid, MenuItem, SelectChangeEvent, Typography } from '@mui/material';
import { FC, useCallback } from 'react';
import type { PaginationComponentProps, PaginationOptions } from 'react-data-table-component';
import { SelectBox } from '../selectbox';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { getNumberOfPages, currentRowsDisplayed } from './tableUtils';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { defaultRowsPerPageOptions, savePerPage } from './table.helpers';

const actionButtonStyle = { minWidth: '32px', padding: '3px', fontSize: '8px' };
const iconStyle = { fontSize: '16px' };

const getDefaultOptions = (t: TFunction): ExtendedPaginationOptions => ({
  noRowsPerPage: false,
  rowsPerPageText: t('table.rowsPerPage'),
  rangeSeparatorText: t('table.rangeSeparator'),
  selectAllRowsItem: false,
  selectAllRowsItemText: t('table.allRows'),
});

type ExtendedPaginationOptions = PaginationOptions & { tableId?: string };

export type TablePaginationProps = PaginationComponentProps & {
  paginationComponentOptions?: ExtendedPaginationOptions;
  paginationRowsPerPageOptions?: number[];
};

export const TablePagination: FC<TablePaginationProps> = ({
  rowsPerPage,
  rowCount,
  onChangePage,
  onChangeRowsPerPage,
  currentPage,
  paginationComponentOptions,
  paginationRowsPerPageOptions = defaultRowsPerPageOptions,
}) => {
  const { t } = useTranslation();
  const defaultPaginationOptions = getDefaultOptions(t);
  const paginationOptions = paginationComponentOptions
    ? { ...defaultPaginationOptions, ...paginationComponentOptions }
    : defaultPaginationOptions;
  const { tableId } = paginationOptions;
  const numberOfPages = getNumberOfPages(rowCount, rowsPerPage);
  const [startRow, endRow] = currentRowsDisplayed(currentPage, rowCount, rowsPerPage);

  const handleRowsPerPageChanged = useCallback(
    ({ target }: SelectChangeEvent<number>) => {
      if (tableId) savePerPage(tableId, Number(target.value));
      onChangeRowsPerPage(Number(target.value), currentPage);
    },
    [currentPage, onChangeRowsPerPage, tableId],
  );

  return (
    <Grid container direction="row" justifyContent="space-between" alignItems="center" gap="10px" marginTop="16px">
      <Grid item>
        <Grid container direction="row" justifyContent="flex-end" alignItems="center" gap="10px">
          <Typography variant="p16">{paginationOptions.rowsPerPageText}</Typography>
          <SelectBox
            fullWidth={false}
            value={rowsPerPage}
            sx={{
              margin: 0,
              '& .MuiOutlinedInput-root': {
                height: '32px',
              },
            }}
            onChange={handleRowsPerPageChanged}
            inputProps={{
              'data-cy': 'tablePagination_rowsPerPage',
            }}
          >
            {paginationRowsPerPageOptions.map(option => (
              <MenuItem key={option} value={option} data-cy={`tablePagination_rowsPerPage_${option}`}>
                {option}
              </MenuItem>
            ))}
          </SelectBox>
        </Grid>
      </Grid>
      <Grid item>
        <Grid container direction="row" justifyContent="flex-end" alignItems="center" gap="10px">
          <Button
            variant="outlined"
            size="small"
            sx={actionButtonStyle}
            disabled={currentPage === 1}
            onClick={() => onChangePage(1, rowCount)}
            data-cy="tablePagination_firstPage"
          >
            <FirstPageIcon sx={iconStyle} />
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={actionButtonStyle}
            disabled={currentPage === 1}
            onClick={() => onChangePage(currentPage - 1, rowCount)}
            data-cy="tablePagination_previousPage"
          >
            <KeyboardArrowLeftIcon sx={iconStyle} />
          </Button>
          <Typography variant="p16">
            {startRow} - {endRow} {paginationOptions.rangeSeparatorText} {rowCount}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            sx={actionButtonStyle}
            disabled={currentPage === numberOfPages}
            onClick={() => onChangePage(currentPage + 1, rowCount)}
            data-cy="tablePagination_nextPage"
          >
            <KeyboardArrowRightIcon sx={iconStyle} />
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={actionButtonStyle}
            disabled={currentPage === numberOfPages}
            onClick={() => onChangePage(numberOfPages, rowCount)}
            data-cy="tablePagination_lastPage"
          >
            <LastPageIcon sx={iconStyle} />
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};
